import { yupResolver } from "@hookform/resolvers/yup";
import { Checkbox, FormControlLabel, TextField } from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from "@stripe/react-stripe-js";
import { Dispatch, SetStateAction, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { StripeField } from "@/components/booking/payment/stripeField";
import SlideBottomDialog from "@/components/common/modals/slideBottomDialog";
import useCreditCardInputs from "@/hooks/payments/useCreditCardInputs";

type FormInput = {
  zipCode: string;
  shouldSaveCard: boolean;
};

const getSchema = (shouldAskForSavingCard: boolean) =>
  yup.object({
    shouldSaveCard: shouldAskForSavingCard ? yup.boolean() : undefined,
    zipCode: yup
      .string()
      .test(
        "zipCode",
        "Please provide valid US zip code",
        (value: string) => !Number.isNaN(+value) && value.length === 5
      ),
  });

export default function AddCreditCardDrawer({
  showDrawer,
  setShowDrawer,
  onConfirm,
  actionPending,
  shouldAskForSavingCard = true,
  defaultSaveCardAfterPayment,
  buttonTitle,
}: {
  showDrawer: boolean;
  setShowDrawer: Dispatch<SetStateAction<boolean>>;
  onConfirm: (newCardOptions?: {
    zipCode: string;
    shouldSaveCard: boolean;
  }) => void;
  actionPending: boolean;
  shouldAskForSavingCard?: boolean;
  defaultSaveCardAfterPayment?: boolean;
  buttonTitle?: string;
}) {
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm<FormInput>({
    resolver: yupResolver(getSchema(shouldAskForSavingCard)),
  });

  const {
    paymentCardValidationInfo,
    isPaymentCardInfoValid,
    showPaymentCardEmptyFieldErrors,
    setShowPaymentCardEmptyFieldErrors,
    onElementChange,
  } = useCreditCardInputs();

  const {
    cardNumberError,
    expiredError,
    cvcError,
    cardNumberComplete,
    expiredComplete,
    cvcComplete,
  } = paymentCardValidationInfo;

  const onSubmit = (data: FormInput) => {
    if (!isPaymentCardInfoValid || !!errors.zipCode) {
      setShowPaymentCardEmptyFieldErrors(true);
      return;
    }
    return onConfirm(data);
  };

  useEffect(() => {
    reset();
    setShowPaymentCardEmptyFieldErrors(false);
  }, [showDrawer, reset, setShowPaymentCardEmptyFieldErrors]);

  return (
    <SlideBottomDialog
      open={showDrawer}
      title="Enter card details"
      submitButtonText={buttonTitle || "Finalize payment"}
      isDisabled={actionPending}
      onClose={() => setShowDrawer(false)}
      onSubmit={handleSubmit(onSubmit)}
    >
      <form>
        <Grid container spacing={2}>
          <Grid size={12}>
            <StripeField
              stripeElement={CardNumberElement}
              error={
                Boolean(cardNumberError) ||
                (!cardNumberComplete && showPaymentCardEmptyFieldErrors)
              }
              labelErrorMessage={cardNumberError}
              onChange={onElementChange(
                "cardNumberComplete",
                "cardNumberError"
              )}
            />
          </Grid>

          <Grid size={6}>
            <StripeField
              stripeElement={CardExpiryElement}
              error={
                Boolean(expiredError) ||
                (!expiredComplete && showPaymentCardEmptyFieldErrors)
              }
              labelErrorMessage={expiredError}
              onChange={onElementChange("expiredComplete", "expiredError")}
            />
          </Grid>

          <Grid size={6}>
            <StripeField
              stripeElement={CardCvcElement}
              error={
                Boolean(cvcError) ||
                (!cvcComplete && showPaymentCardEmptyFieldErrors)
              }
              labelErrorMessage={cvcError}
              onChange={onElementChange("cvcComplete", "cvcError")}
            />
          </Grid>

          <Grid size={12}>
            <Controller
              defaultValue=""
              name="zipCode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  data-dd-privacy="mask"
                  slotProps={{
                    inputLabel: {
                      shrink: true,
                    },
                  }}
                  fullWidth
                  type="text"
                  placeholder="Billing zip code"
                  error={!!errors.zipCode}
                  helperText={
                    errors.zipCode ? (errors.zipCode.message as string) : ""
                  }
                />
              )}
            />
          </Grid>
          {shouldAskForSavingCard && (
            <Grid size={12}>
              <Controller
                name="shouldSaveCard"
                control={control}
                defaultValue={defaultSaveCardAfterPayment ?? false}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Checkbox
                        sx={{ py: 0 }}
                        {...field}
                        checked={field.value}
                      />
                    }
                    label="Save the card for future payment"
                  />
                )}
              />
            </Grid>
          )}
        </Grid>
      </form>
    </SlideBottomDialog>
  );
}
